import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Swords,
  Clock,
  Gauge,
  RotateCcw,
  SlidersHorizontal,
  Dices,
  Sparkles,
  Check,
  HelpCircle,
  Lightbulb,
  Eye,
  EyeOff,
  BookOpen,
  GraduationCap,
  ChevronLeft,
  ClipboardList,
  Gamepad2,
} from "lucide-react";
import MatchmakingSpinner from "@/components/MatchmakingSpinner";
import CategoryPicker from "@/components/CategoryPicker";
import FacetPicker from "@/components/FacetPicker";
import CompletionCard from "@/components/CompletionCard";
import LibraryEntryDrawer from "@/components/LibraryEntryDrawer";
import AIFeedbackPanel from "@/components/AIFeedbackPanel";
import RichTextEditor from "@/components/RichTextEditor";
import Markdown from "@/components/Markdown";
import { useCategoryStore } from "@/store/categoryStore";
import { useFacetStore } from "@/store/facetStore";
import { useProgressStore } from "@/store/progressStore";
import { generateChallenge, templatesForDisciplines } from "@/content/challenges";
import { contextSummary } from "@/content/facets";
import { facetExamples } from "@/content/facetExamples";
import { categoryName, useChallenges, getChallenges, useConceptAxes } from "@/store/customContentStore";
import { useLibraryContentStore } from "@/store/libraryContentStore";
import type { GeneratedChallenge, Difficulty } from "@/core/data/types";
import type { CompletionResult } from "@/core/data/db";

type Phase = "idle" | "searching" | "result";
type Panel = "none" | "disciplines" | "concept";

const XP_BY_DIFFICULTY: Record<Difficulty, number> = { Easy: 10, Medium: 20, Hard: 30 };
const draftKey = (id: string) => `hippo.draft.${id}`;

export default function Challenge() {
  const active = useCategoryStore((s) => s.active);
  const axisCount = useFacetStore((s) => s.activeAxisCount());
  const complete = useProgressStore((s) => s.complete);
  // Library sub-sessions tied to a discipline (for "Study this").
  const entriesForDiscipline = useLibraryContentStore((s) => s.entriesForDiscipline);
  const challenges = useChallenges();
  const conceptAxes = useConceptAxes();
  const axisLabels = useMemo(() => Object.fromEntries(conceptAxes.map((a) => [a.key, a.label])), [conceptAxes]);

  const [phase, setPhase] = useState<Phase>("idle");
  const [panel, setPanel] = useState<Panel>("none");
  const [gen, setGen] = useState<GeneratedChallenge | null>(null);
  const [response, setResponse] = useState("");
  const [completion, setCompletion] = useState<CompletionResult | null>(null);
  // guidance / self-assessment
  const [showHints, setShowHints] = useState(false);
  const [showPoints, setShowPoints] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [covered, setCovered] = useState<Set<number>>(new Set());
  const [studyEntryId, setStudyEntryId] = useState<string | null>(null);
  const [tip, setTip] = useState<{ value: string; examples: string[]; x: number; y: number } | null>(null);

  const lastId = useRef<string | null>(null);
  const timer = useRef<number | null>(null);

  const poolCount = useMemo(() => templatesForDisciplines(active, challenges).length, [active, challenges]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (gen) localStorage.setItem(draftKey(gen.template.id), response);
  }, [response, gen]);

  function resetGuidance() {
    setShowHints(false);
    setShowPoints(false);
    setShowExample(false);
    setCovered(new Set());
    setStudyEntryId(null);
  }

  function findChallenge() {
    if (poolCount === 0) return;
    setCompletion(null);
    setPhase("searching");
    const selection = useFacetStore.getState().activeSelection();
    timer.current = window.setTimeout(() => {
      const next = generateChallenge(active, selection, lastId.current, getChallenges());
      if (!next) return setPhase("idle");
      lastId.current = next.template.id;
      setGen(next);
      setResponse(localStorage.getItem(draftKey(next.template.id)) ?? "");
      resetGuidance();
      setPhase("result");
    }, 2600);
  }

  function cancel() {
    if (timer.current) window.clearTimeout(timer.current);
    setPhase("idle");
  }

  async function submit() {
    if (!gen) return;
    const total = gen.template.idealPoints.length;
    // Record the self-assessment whenever the user actually ticked points,
    // regardless of whether the Key Points panel is expanded at save time.
    const score = covered.size > 0 && total > 0 ? Math.round((covered.size / total) * 100) : undefined;
    const xp = XP_BY_DIFFICULTY[gen.template.difficulty] + (response.trim().length >= 150 ? 5 : 0);
    const result = await complete(
      {
        refType: "challenge",
        refId: gen.template.id,
        categoryIds: gen.disciplineIds,
        title: gen.template.title,
        context: contextSummary(gen.context),
        response: response.trim(),
        score,
        createdAt: Date.now(),
      },
      xp
    );
    localStorage.removeItem(draftKey(gen.template.id));
    setCompletion(result);
  }

  function toggleCovered(i: number) {
    setCovered((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  // ---- Searching ----
  if (phase === "searching") {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <MatchmakingSpinner onCancel={cancel} />
      </div>
    );
  }

  // ---- Result ----
  if (phase === "result" && gen) {
    const t = gen.template;
    const presentFacets = Object.keys(gen.context);
    const studyEntries = entriesForDiscipline(gen.disciplineIds);
    const total = t.idealPoints.length;
    const score = total > 0 ? Math.round((covered.size / total) * 100) : 0;

    return (
      <>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 24 }}
        className="mx-auto max-w-3xl space-y-5"
      >
        <button onClick={cancel} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {presentFacets.length > 0 && (
          <div className="rounded-2xl border border-border bg-surface p-6">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
              <Sparkles className="h-4 w-4 text-accent" /> Your rolled game concept
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {presentFacets.map((key) => {
                const value = gen.context[key];
                const examples = facetExamples(key, value);
                return (
                  <div
                    key={key}
                    onMouseEnter={(e) => {
                      if (!examples.length) return;
                      const r = e.currentTarget.getBoundingClientRect();
                      setTip({ value, examples, x: r.left + r.width / 2, y: r.top });
                    }}
                    onMouseLeave={() => setTip(null)}
                    className={[
                      "flex items-center justify-between gap-3 rounded-lg bg-surface-2 px-3 py-2",
                      examples.length ? "cursor-help ring-1 ring-transparent transition-shadow hover:ring-primary/40" : "",
                    ].join(" ")}
                  >
                    <span className="text-xs uppercase tracking-wide text-muted">{axisLabels[key] ?? key}</span>
                    <span className="text-right text-sm font-medium text-text">{value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-surface p-7">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {t.disciplineIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 font-medium text-primary">
                {categoryName(id)}
              </span>
            ))}
            <Badge icon={Gauge}>{t.difficulty}</Badge>
            <Badge icon={Clock}>{t.estMinutes} min</Badge>
          </div>

          <h2 className="mt-4 font-display text-2xl font-bold text-text">{t.title}</h2>
          <div className="mt-2 text-muted"><Markdown source={t.task} /></div>

          <div className="mt-5 flex gap-3 rounded-xl border border-accent/30 bg-accent/5 p-4">
            <HelpCircle className="h-5 w-5 shrink-0 text-accent" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-accent">Keep in mind</div>
              <p className="mt-0.5 text-sm text-text">{t.question}</p>
            </div>
          </div>

          {t.deliverable && (
            <div className="mt-4 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
              <ClipboardList className="h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-primary">Deliverable — make something real</div>
                <p className="mt-0.5 text-sm text-text">{t.deliverable}</p>
              </div>
            </div>
          )}

          <div className="mt-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">Constraints</div>
            <ul className="mt-2 flex flex-wrap gap-2">
              {gen.constraints.map((c) => (
                <li key={c} className="rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-text">{c}</li>
              ))}
            </ul>
          </div>

          {/* Guidance */}
          <div className="mt-5 flex flex-wrap gap-2">
            <GuideBtn icon={Lightbulb} active={showHints} onClick={() => setShowHints((v) => !v)}>
              {showHints ? "Hide hints" : "Hints"}
            </GuideBtn>
            <GuideBtn icon={showPoints ? EyeOff : Eye} active={showPoints} onClick={() => setShowPoints((v) => !v)}>
              {showPoints ? "Hide key points" : "Key points"}
            </GuideBtn>
            <GuideBtn icon={BookOpen} active={showExample} onClick={() => setShowExample((v) => !v)}>
              {showExample ? "Hide example" : "Example approach"}
            </GuideBtn>
          </div>

          {showHints && (
            <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 space-y-1.5 overflow-hidden rounded-xl border border-border bg-surface-2/60 p-4 text-sm text-muted">
              {t.hints.map((h) => (
                <li key={h} className="flex items-start gap-2"><Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />{h}</li>
              ))}
            </motion.ul>
          )}

          {showPoints && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden rounded-xl border border-accent/30 bg-accent/5 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-accent">What a strong answer covers — tap what you addressed</span>
                <span className="text-xs font-medium text-text">{covered.size}/{total} · {score}%</span>
              </div>
              <ul className="space-y-1.5">
                {t.idealPoints.map((p, i) => {
                  const on = covered.has(i);
                  return (
                    <li key={p}>
                      <button onClick={() => toggleCovered(i)} className="flex w-full items-start gap-2 rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-accent/10">
                        <span className={["mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border", on ? "border-accent bg-accent text-bg" : "border-border"].join(" ")}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="text-text">{p}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}

          {showExample && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden rounded-xl border border-border bg-surface-2/60 p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Example approach</div>
              <div className="text-sm leading-relaxed text-muted"><Markdown source={t.exampleApproach} /></div>
            </motion.div>
          )}

          {/* Related study */}
          {studyEntries.length > 0 && (
            <div className="mt-5 border-t border-border pt-4">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                <GraduationCap className="h-4 w-4 text-primary" /> Study this
              </div>
              <div className="flex flex-wrap gap-2">
                {studyEntries.slice(0, 5).map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setStudyEntryId(e.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text transition-colors hover:border-primary/50 hover:bg-surface-2"
                  >
                    <BookOpen className="h-3.5 w-3.5 text-primary" />
                    {e.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {completion ? (
          <div className="space-y-3">
            {covered.size > 0 && (
              <div className="rounded-xl border border-border bg-surface px-4 py-2.5 text-center text-sm text-muted">
                Self-assessment: addressed <span className="text-text">{covered.size}/{total}</span> key points ({score}%)
              </div>
            )}
            <CompletionCard result={completion} onNext={findChallenge} nextLabel="Next challenge" />
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-surface p-7">
            <label className="text-sm font-medium text-text">Your response</label>
            <div className="mt-2">
              <RichTextEditor
                value={response}
                onChange={setResponse}
                placeholder="Reflect and write your design solution here… (autosaved)"
                minHeight="9rem"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={submit}
                disabled={response.trim().length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Check className="h-4 w-4" />
                Save &amp; complete
              </button>
              <button
                onClick={findChallenge}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40"
              >
                <RotateCcw className="h-4 w-4" />
                Reroll
              </button>
              {response.trim().length > 0 ? (
                <AIFeedbackPanel
                  buildRequest={() => ({
                    kind: "challenge",
                    title: t.title,
                    task: t.task,
                    context: contextSummary(gen.context),
                    idealPoints: t.idealPoints,
                    response: response.trim(),
                  })}
                />
              ) : (
                <span className="ml-auto text-xs text-muted/70">Write a response to get AI feedback</span>
              )}
            </div>
          </div>
        )}
      </motion.div>

      <LibraryEntryDrawer
        entryId={studyEntryId}
        relatedIds={studyEntries.map((e) => e.id)}
        onClose={() => setStudyEntryId(null)}
      />

      {/* Real-game examples on hover — same as the Game Concept picker. */}
      {tip && (
        <div
          style={{ position: "fixed", left: tip.x, top: tip.y - 8, transform: "translate(-50%, -100%)", zIndex: 60 }}
          className="pointer-events-none w-56 rounded-xl border border-border bg-surface p-3 text-left shadow-2xl"
        >
          <div className="font-display text-sm font-semibold text-text">{tip.value}</div>
          <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
            <Gamepad2 className="h-3 w-3" /> Real examples
          </div>
          <div className="mt-0.5 text-xs text-text/90">{tip.examples.join(" · ")}</div>
        </div>
      )}
      </>
    );
  }

  // ---- Idle ----
  return (
    <div className="mx-auto max-w-2xl">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-2 text-primary">
          <Swords className="h-8 w-8" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-text">Ready for a design challenge?</h2>
          <p className="mt-2 text-muted">
            Hippo rolls a game concept from your <span className="text-text">{axisCount}</span> active{" "}
            {axisCount === 1 ? "axis" : "axes"} and pairs it with a task from your{" "}
            <span className="text-text">{active.length}</span> {active.length === 1 ? "discipline" : "disciplines"} ({poolCount} available).
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={findChallenge} disabled={poolCount === 0} className="rounded-xl bg-primary px-6 py-3 font-display font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
            Find a Challenge
          </button>
          <button onClick={() => setPanel(panel === "disciplines" ? "none" : "disciplines")} className={toolBtn(panel === "disciplines")}>
            <SlidersHorizontal className="h-4 w-4" />
            Disciplines
          </button>
          <button onClick={() => setPanel(panel === "concept" ? "none" : "concept")} className={toolBtn(panel === "concept")}>
            <Dices className="h-4 w-4" />
            Game Concept
          </button>
        </div>

        {poolCount === 0 && <p className="text-sm text-amber-400">No disciplines active — enable some to get a challenge.</p>}
      </div>

      {panel !== "none" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-2xl border border-border bg-surface p-6">
          <h3 className="mb-1 font-display text-lg font-semibold text-text">{panel === "disciplines" ? "Disciplines" : "Game Concept"}</h3>
          <p className="mb-4 text-sm text-muted">
            {panel === "disciplines"
              ? "Choose which design/craft subjects your challenges are drawn from."
              : "Enable or disable each axis, and pick exactly which values can be rolled. Everything is on by default."}
          </p>
          {panel === "disciplines" ? <CategoryPicker /> : <FacetPicker />}
        </motion.div>
      )}
    </div>
  );
}

function toolBtn(active: boolean): string {
  return [
    "inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors",
    active ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-text hover:bg-surface-2",
  ].join(" ");
}

function GuideBtn({
  icon: Icon,
  children,
  active,
  onClick,
}: {
  icon: typeof Lightbulb;
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
        active ? "border-primary bg-primary/10 text-text" : "border-border bg-surface-2 text-text hover:bg-border/40",
      ].join(" ")}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

function Badge({ icon: Icon, children }: { icon: typeof Clock; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-3 py-1 font-medium text-muted">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}
